import React, { useEffect, useState } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import './AdminOurTeam.css';
import { Circles } from 'react-loader-spinner';
import privateAxios from '../../utils/axios/privateAxios';
import { FaEdit, FaTrash } from 'react-icons/fa';
import moment from 'moment';
import ConfirmDialog from '../../components/ConfirmDialog/ConfirmDialog';
import Notification from '../../components/Notification/Notification';

const AdminOurTeam = () => {
    const navigate = useNavigate();
    const [notification, setNotification] = useState({ type: '', message: '' });
    const [posts, setPosts] = useState([]);
    const [selectedPosts, setSelectedPosts] = useState([]);
    const [loading, setLoading] = useState(false);
    const [confirmDialog, setConfirmDialog] = useState({
        open: false,
        message: '',
        onConfirm: null,
        loading: false,
    });

    const showNotification = (type, message) => {
        setNotification({ type, message });
        setTimeout(() => {
            setNotification({ type: '', message: '' });
        }, 5000);
    };

    const fetchPosts = async () => {
        try {
            setLoading(true);
            const response = await privateAxios.get(`/private/admin-our-team`);
            if (response.status === 200) {
                setPosts(response.data.posts);
            }
        } catch (error) {
            console.error('Error fetching posts', error);
            setPosts([]);
        } finally {
            setLoading(false);
        }
    };

    useEffect(() => {
        fetchPosts();
    }, []);

    const handleCheckbox = (id) => {
        setSelectedPosts((prev) =>
            prev.includes(id) ? prev.filter((p) => p !== id) : [...prev, id]
        );
    };

    const deleteSelectedPosts = async () => {
        if (!selectedPosts.length) return;
        setConfirmDialog({
            open: true,
            message: `Are you sure you want to delete ${selectedPosts.length} selected team member${selectedPosts.length > 1 ? 's' : ''}?`,
            onConfirm: async () => {
                try {
                    setConfirmDialog((prev) => ({ ...prev, loading: true }));
                    const response = await privateAxios.post('/private/delete-selected-team-members', { ids: selectedPosts });
                    setSelectedPosts([]);
                    setConfirmDialog({ open: false, message: '', onConfirm: null, loading: false });
                    if (response.status === 200) {
                        setNotification({ type: 'success', message: response.data.message });
                        setTimeout(() => {
                            setNotification({ type: '', message: '' });
                        }, 2500);
                    }
                    fetchPosts();
                } catch (error) {
                    console.error('Error deleting posts', error);
                    setConfirmDialog((prev) => ({ ...prev, loading: false }));
                }
            },
            loading: false
        });
    };

    const handleDeleteSingle = (id, title) => {
        setConfirmDialog({
            open: true,
            message: `Are you sure you want to delete the team member titled "${title}"?`,
            onConfirm: async () => {
                try {
                    setConfirmDialog((prev) => ({ ...prev, loading: true }));
                    const response = await privateAxios.delete(`/private/delete-team-member/${id}`);
                    setConfirmDialog({ open: false, message: '', onConfirm: null, loading: false });
                    if (response.status === 200) {
                        setNotification({ type: 'success', message: response.data.message });
                        setTimeout(() => {
                            setNotification({ type: '', message: '' });
                        }, 2500);
                    }
                    fetchPosts();
                } catch (error) {
                    console.error('Error deleting post', error);
                    setConfirmDialog((prev) => ({ ...prev, loading: false }));
                }
            },
            loading: false
        });
    };

    const handleEdit = (id) => navigate(`/admin-edit-our-team/${id}`);

    return (
        <div className="admin-posts">
            <Notification
                type={notification.type}
                message={notification.message}
                onClose={() => setNotification({ type: '', message: '' })}
            />
            <div className="admin-posts-header">

                <div className='admin-our-team-title'><h2>Manage Our Team</h2></div>
                <Link to={'/add-to-team'}>Add To Team</Link>

                {posts.length > 0 &&
                    <button
                        className="admin-posts-delete-selected-btn"
                        style={{ backgroundColor: selectedPosts.length ? '#008751' : '#ccf1e4' }}
                        onClick={deleteSelectedPosts}
                        disabled={!selectedPosts.length}
                    >
                        Delete Selected
                    </button>}
            </div>

            <div className="admin-posts-list">
                {loading ? (
                    <div style={{ display: 'flex', flexDirection: 'column', columnGap: '10px', alignItems: 'center', justifyContent: 'center', height: '300px', color: 'gray' }}>
                        <Circles height="50" width="50" color="#008751" ariaLabel="loading" /> <h2>Loading Team Members...</h2>
                    </div>
                ) : posts.length ? (
                    posts.map((p, index) => (
                        <div className="admin-posts-card" key={p._id || index}>
                            <div className='admin-posts-checkbox-title'>
                                <input
                                    type="checkbox"
                                    checked={selectedPosts.includes(p._id)}
                                    onChange={() => handleCheckbox(p._id)}
                                />
                                <div className="admin-posts-info">
                                    <h4>
                                        {p.title.split(' ').length > 10
                                            ? p.title.split(' ').slice(0, 10).join(' ') + '...'
                                            : p.title}
                                    </h4>

                                    <small>
                                        <p>Added By: <strong>{p.createdBy?.firstname} {p.createdBy?.lastname}</strong></p>
                                        <p>On: <strong>{moment(p.createdAt).format('MMMM DD, YYYY')}</strong></p>
                                        {p.updatedAt && ` | Last Edited: ${moment(p.updatedAt).format('MMMM DD, YYYY')}`}
                                    </small>
                                </div>
                            </div>

                            <div className="admin-posts-actions">
                                <button className='admin-posts-edit-button' onClick={() => handleEdit(p.slug)}><FaEdit /></button>
                                <button
                                    className='admin-posts-delete-button'
                                    onClick={() => handleDeleteSingle(p._id, p.title)}
                                >
                                    <FaTrash />
                                </button>
                            </div>
                        </div>
                    ))
                ) : (
                    <div style={{display: 'flex', alignItems: 'center', justifyContent: 'center'}}><p>No Team Member to Display.</p></div>
                )}
            </div>

            <div className="admin-reports-back-to-admin-dashboard">
                <Link to={'/admin-dashboard'}>Back to Management Dashboard</Link>
            </div>

            {confirmDialog.open && (
                <ConfirmDialog
                    message={confirmDialog.message}
                    onConfirm={confirmDialog.onConfirm}
                    onCancel={() => setConfirmDialog({ open: false, message: '', onConfirm: null, loading: false })}
                    loading={confirmDialog.loading}
                />
            )}
        </div>
    );
};

export default AdminOurTeam;